/**
 * House Loan Calculator Form Component
 * Basic mortgage calculator for new home loans
 */
import { useState } from 'react';
import { useCustomTranslation } from '../../../hooks';

export interface HouseLoanData {
  loan_amount: number;
  annual_interest_rate: number;
  loan_term_years: number;
}

export interface HouseLoanResults {
  monthly_payment: number;
  total_payment: number;
  total_interest: number;
  loan_amount: number;
  annual_interest_rate: number;
  loan_term_years: number;
  loan_term_months: number;
}

interface HouseLoanCalculatorFormProps {
  onCalculate: (data: HouseLoanData) => void;
  isLoading?: boolean;
}

export default function HouseLoanCalculatorForm({ 
  onCalculate, 
  isLoading = false 
}: HouseLoanCalculatorFormProps) {
  const { t } = useCustomTranslation();
  
  const [loanAmount, setLoanAmount] = useState('5000000');
  const [interestRate, setInterestRate] = useState('4.0');
  const [loanTermYears, setLoanTermYears] = useState('25');

  const handleNumberInput = (value: string, setter: (val: string) => void) => {
    const sanitized = value.replace(/[^\d.]/g, '');
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      return;
    }
    setter(sanitized);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const data: HouseLoanData = {
      loan_amount: parseFloat(loanAmount) || 0,
      annual_interest_rate: parseFloat(interestRate) || 0,
      loan_term_years: parseInt(loanTermYears) || 0,
    };
    
    onCalculate(data);
  };

  return (
    <form onSubmit={handleSubmit} className="loan-calculator-form">
      {/* Loan Details Section */}
      <div className="form-section">
        <div className="form-group">
          <label htmlFor="loan_amount">
            {t('houseLoan.loanAmount')}
          </label>
          <input
            type="text"
            id="loan_amount"
            inputMode="decimal"
            value={loanAmount}
            onChange={(e) => handleNumberInput(e.target.value, setLoanAmount)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="interest_rate">
            {t('houseLoan.interestRate')}
          </label>
          <input
            type="text"
            id="interest_rate"
            inputMode="decimal"
            value={interestRate}
            onChange={(e) => handleNumberInput(e.target.value, setInterestRate)}
            required
          />
          <small style={{ color: '#666', fontSize: '14px' }}>
            {t('houseLoan.typicalRate')}: 3.5% - 4.5% (2026)
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="loan_term">
            {t('houseLoan.loanTerm')}
          </label>
          <input
            type="text"
            id="loan_term"
            inputMode="numeric"
            value={loanTermYears}
            onChange={(e) => handleNumberInput(e.target.value, setLoanTermYears)}
            required
          />
          <small style={{ color: '#666', fontSize: '14px' }}>
            {t('houseLoan.commonTerms')}: 20, 25, 30 {t('houseLoan.years')}
          </small>
        </div>
      </div>

      <button type="submit" disabled={isLoading}>
        {isLoading ? t('form.calculating') : t('form.calculate')}
      </button>
    </form>
  );
}
